import axios from '@/component/axios'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'

const STORAGE_KEY = 'poda_web_push_token'
const DEVICE_ID_KEY = 'poda_web_push_device_id'

const baseApi = () => (process.env.VUE_APP_APIENDPOINT || '').trim()
const buildUrl = path => `${baseApi().replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`

function readEnvConfig() {
  const keys = {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID,
    vapidKey: process.env.VUE_APP_FIREBASE_VAPID_KEY,
  }

  return Object.fromEntries(
    Object.entries(keys).filter(([, value]) => value !== undefined && value !== ''),
  )
}

async function fetchRemoteConfig() {
  try {
    const response = await axios.get(buildUrl('firebase/web-config'))
    return response.data?.data || {}
  } catch (error) {
    return {}
  }
}

async function resolveFirebaseConfig() {
  const remote = await fetchRemoteConfig()
  const local = readEnvConfig()

  return { ...local, ...remote }
}

function waitForServiceWorker(registration) {
  if (registration.active) {
    return Promise.resolve(registration)
  }

  return new Promise(resolve => {
    const worker = registration.installing || registration.waiting
    if (!worker) {
      resolve(registration)
      return
    }

    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') {
        resolve(registration)
      }
    })
  })
}

function showBrowserNotification(payload) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return
  }

  const title = payload.notification?.title || payload.data?.title || 'My PODA'
  const body = payload.notification?.body || payload.data?.body || ''
  const icon = payload.notification?.icon || payload.data?.icon || '/favicon.ico'

  try {
    new Notification(title, {
      body,
      icon,
      data: payload.data || {},
    })
  } catch (error) {
    // Ignore when Notification constructor is blocked.
  }
}

let initialized = false

export async function initAdminWebPushNotifications({
  registerToken,
  onNotificationClick,
  onForegroundMessage,
} = {}) {
  if (initialized || typeof window === 'undefined') {
    return false
  }

  if (!(await isSupported())) {
    return false
  }

  const config = await resolveFirebaseConfig()
  if (!config.apiKey || !config.vapidKey) {
    return false
  }

  if (!('Notification' in window)) {
    return false
  }

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }

  if (permission !== 'granted') {
    return false
  }

  const app = initializeApp(config)
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
  await waitForServiceWorker(registration)

  if (registration.active) {
    registration.active.postMessage({ type: 'INIT_FIREBASE', config })
  }

  const messaging = getMessaging(app)
  const deviceId = localStorage.getItem(DEVICE_ID_KEY) || `web-admin-${Date.now()}`
  localStorage.setItem(DEVICE_ID_KEY, deviceId)

  const token = await getToken(messaging, {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: registration,
  })

  if (token && token !== localStorage.getItem(STORAGE_KEY)) {
    await registerToken({
      fcm_token: token,
      device_id: deviceId,
      platform: 'web',
    })
    localStorage.setItem(STORAGE_KEY, token)
  }

  onMessage(messaging, payload => {
    showBrowserNotification(payload)
    if (typeof onForegroundMessage === 'function') {
      onForegroundMessage(payload)
    }
  })

  navigator.serviceWorker.addEventListener('message', event => {
    if (event.data?.type === 'NOTIFICATION_CLICK' && typeof onNotificationClick === 'function') {
      onNotificationClick(event.data.data || {})
    }
  })

  initialized = true
  return true
}

export async function unregisterAdminWebPushToken(removeToken) {
  const token = localStorage.getItem(STORAGE_KEY)
  const deviceId = localStorage.getItem(DEVICE_ID_KEY)

  if (token && typeof removeToken === 'function') {
    await removeToken({
      fcm_token: token,
      device_id: deviceId || undefined,
    })
  }

  localStorage.removeItem(STORAGE_KEY)
}
